<!-- Component can be used in microscope and periscope -->
<template>
  <LabelAndHint
    v-slot="{ inputId }"
    v-bind="{ ...props, error: props.error || invalid, errorMessages: props.errorMessages || errorMessages }"
  >
    <div
      :id="inputId"
      ref="groupElement"
      class="radio-group"
      :class="{
        row: isRow,
        disabled: disabled,
      }"
      role="radiogroup"
      :aria-disabled="disabled"
    >
      <label
        v-for="option in options"
        :key="option.value"
        class="radio-option"
        :class="{ disabled: disabled || option.disabled }"
      >
        <input
          :name="name ?? inputId"
          type="radio"
          :value="option.value"
          :checked="option.value === currentValue"
          :disabled="disabled || option.disabled"
          @input="onInput"
          @blur="onBlur"
        />
        <span>{{ option.text }}</span>
      </label>
    </div>
  </LabelAndHint>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import type { ValidationRuleObject } from '@staff/shared'

import { useInputValidation } from '../../composables'
import LabelAndHint from '../AppTextField/LabelAndHint.vue'

const props = defineProps<{
  id?: string | number

  // For shared components to work in both vue 2.7 and vue 3 we need to support both `value` and `modelValue`
  // When periscope is updated to vue 3, we can remove `value` and only use `modelValue`
  value?: string | null
  modelValue?: string | null

  options: { text: string; value: string; disabled?: boolean }[]
  name?: string
  direction?: 'row' | 'column'

  label?: string
  hint?: string | string[]
  hideDetails?: boolean

  error?: boolean
  errorMessages?: string | string[]

  disabled?: boolean

  rules?: (ValidationRuleObject | null)[]
  validationOptions?: {
    validateOnModelChange?: boolean
  }
}>()

const emit = defineEmits<{
  (e: 'input', v: string): void
  (e: 'update:modelValue', v: string): void
  (e: 'blur'): void
}>()

const groupElement = ref<HTMLElement | null | undefined>(undefined)
const { invalid, errorMessages, validate } = useInputValidation(
  () => currentValue.value,
  () => props.rules,
  groupElement,
)

// For vue 2.7 and vue 3 compatibility
const currentValue = computed(() => props.modelValue ?? props.value ?? null)

const isRow = computed(() => props.direction === 'row')

const onInput = (event: Event) => {
  if (event.target instanceof HTMLInputElement) {
    emit('input', event.target.value)
    emit('update:modelValue', event.target.value)
  }

  if ((props.rules?.length ?? 0) > 0) {
    nextTick(() => validate())
  }
}

const onBlur = () => {
  emit('blur')

  if ((props.rules?.length ?? 0) > 0) {
    nextTick(() => validate())
  }
}

watch(
  () => currentValue.value,
  () => {
    if (invalid.value || props.validationOptions?.validateOnModelChange) {
      nextTick(() => validate())
    }
  },
)
</script>

<style lang="scss" scoped>
.radio-group {
  display: grid;
  gap: 8px;

  &.row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  &.disabled {
    opacity: 0.7;
  }
}

.radio-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--color-fg-default);

  input {
    margin: 0;
  }

  &.disabled {
    color: var(--color-fg-disabled);
    cursor: not-allowed;
  }
}
</style>
